import type { RadiusId, TimeRangeId } from '@talkofthetown/shared';
import type { User } from 'firebase/auth';
import { useMediaQuery } from '../hooks/misc';
import type { AuthState } from '../hooks/useAuth';
import type { LocationStatus } from '../hooks/useViewerLocation';
import type { ChosenPlace } from '../lib/places';
import { Logo } from './Logo';
import { Menu } from './Menu';
import { PlaceMenu } from './PlaceMenu';
import { ScopeMenu } from './ScopeMenu';

interface Props {
  auth: AuthState;
  /** The distance shown, measured from the chosen place or the visitor's location. */
  radius: RadiusId;
  /** How far back the board looks. */
  range: TimeRangeId;
  place: ChosenPlace | null;
  followingLocation: boolean;
  locationStatus: LocationStatus;
  /** The board shown is out of date (the reader hasn't refreshed since it changed). */
  changed: boolean;
  /** Where the board looks, e.g. "within 10 km of Kochi". */
  changedScope: string;
  onRefresh: () => void;
  onRadiusChange: (r: RadiusId) => void;
  onRangeChange: (r: TimeRangeId) => void;
  onChoosePlace: (place: ChosenPlace) => void;
  onUseMyLocation: () => void;
  onCompose: () => void;
  onSignIn: () => void;
  onReview: () => void;
  onStats: () => void;
  onMyPosts: () => void;
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4v4.5H15"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Refresh (an icon; its name is the tooltip): only there once what the board would show has changed
 * (new posts, other ranks or sizes), pulsing until it's pressed. The board never moves before that.
 */
function RefreshButton({ scope, onRefresh }: { scope: string; onRefresh: () => void }) {
  const label = `Refresh: the board ${scope} has changed since you last refreshed`;
  return (
    <button
      type="button"
      className="refresh-btn"
      aria-label={label}
      title={label}
      onClick={onRefresh}
    >
      <RefreshIcon />
    </button>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8.5" r="4" fill="currentColor" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0z" fill="currentColor" />
    </svg>
  );
}

/** The name and photo of a signed-in account, from its Google profile if the account has none. */
function profileOf(user: User | null) {
  const google = user?.providerData.find((p) => p.providerId === 'google.com');
  return {
    name: (user?.displayName || google?.displayName || '').trim(),
  };
}

/**
 * The account as a little ink-outlined bubble with their initial (or a person icon), in the page's
 * own style rather than Google's coloured profile picture.
 */
function Avatar({ user }: { user: User | null }) {
  const { name } = profileOf(user);
  return (
    <span className="avatar avatar--blank" aria-hidden="true">
      {name ? [...name][0]!.toUpperCase() : <PersonIcon />}
    </span>
  );
}

export function Header(p: Props) {
  // Narrow screens: "10 km · 24 h", so the place, distance, time and + Talk share a row.
  const short = useMediaQuery('(max-width: 1100px)');
  const account = profileOf(p.auth.user);
  return (
    <header className="topbar">
      <a className="brand" href="/" aria-label="Talk of the Town, home">
        <Logo />
      </a>
      {/* Where and how far back the board looks: "Near me | Within 10 km · Last 24 hours". */}
      <div className="topbar__scope">
        <PlaceMenu
          place={p.place}
          followingLocation={p.followingLocation}
          status={p.locationStatus}
          onChoose={p.onChoosePlace}
          onUseMyLocation={p.onUseMyLocation}
        />
        <ScopeMenu
          radius={p.radius}
          range={p.range}
          short={short}
          onRadius={p.onRadiusChange}
          onRange={p.onRangeChange}
        />
      </div>
      <div className="topbar__actions">
        {p.changed && <RefreshButton scope={p.changedScope} onRefresh={p.onRefresh} />}
        <button type="button" className="btn btn--primary" onClick={p.onCompose}>
          + Talk
        </button>
      </div>
      <div className="topbar__tools">
        {p.auth.signedIn ? (
          <Menu
            label={account.name ? `Account: ${account.name}` : 'Account'}
            className="account"
            panelClassName="account__menu"
            summary={<Avatar user={p.auth.user} />}
          >
            <p className="account__name">{account.name || 'Signed in'}</p>
            <button type="button" className="btn btn--small btn--ghost" onClick={p.onMyPosts}>
              My posts
            </button>
            {p.auth.admin && (
              <>
                <p className="muted">Admin</p>
                <button type="button" className="btn btn--small btn--ghost" onClick={p.onStats}>
                  App stats
                </button>
                <button type="button" className="btn btn--small btn--ghost" onClick={p.onReview}>
                  Review held posts
                </button>
              </>
            )}
            <button type="button" className="btn btn--small" onClick={() => void p.auth.signOut()}>
              Sign out
            </button>
          </Menu>
        ) : (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={p.onSignIn}
            disabled={!p.auth.ready}
          >
            Sign in
          </button>
        )}
      </div>
    </header>
  );
}
